import React, { useCallback, useContext, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, CardActionArea, CardContent, Typography } from '@mui/material';
import { capitalize } from 'helpers/helpers';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import {
    useDeleteHrSimulation,
    useDuplicateHrSimulation,
    usePatchHrSimulation,
} from 'routes/HumanResourcesModule/Simulation/helpers/hooks/simulation';
import {
    HrSimulation,
    HrSimulationDuplicatePayload,
    PatchHrSimulationPayload,
} from 'routes/HumanResourcesModule/Simulation/helpers/types';
import SimulationItemTitle from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationItem/SimulationItemTitle';
import SimulationTypeChip from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationItem/SimulationTypeChip';
import SimulationModal from 'routes/HumanResourcesModule/Simulation/SimulationHome/components/SimulationModal/SimulationModal';
import StyCard from 'routes/HumanResourcesModule/components/ui/StyCard';
import HistoricalOrgChartHrContext from 'routes/HumanResourcesModule/Simulation/contexts/ObsoleteOrgChartSimulationContext';
import { ObsoleteInfoBox } from 'routes/HumanResourcesModule/components/ui/ObsoleteInfoBox';
import Flex from 'ui/Flex/Flex';
import ModalForm from 'components/Form/ModalForm';

const SimulationCardContent = styled(CardContent)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: '#fff',
    padding: theme.spacing(2),
    height: '100%',
}));

const SimulationTitleWrapper = styled(Box)({
    flex: 1,
    maxHeight: '70%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1em',
});

const SimulationChipWrapper = styled(Box)({
    flex: 1,
    maxHeight: '30%',
});

type HrSimulationItemProps = {
    hrSimulation: HrSimulation;
};

const HrSimulationItem = ({
    hrSimulation,
}: HrSimulationItemProps): React.ReactElement => {
    const { t } = useTranslation();
    const { data: obsoleteOrgChartHr } = useContext(
        HistoricalOrgChartHrContext,
    );
    const obsoleteOrgChartHrLength = Object.keys(obsoleteOrgChartHr).includes(
        hrSimulation.id.toString(),
    )
        ? obsoleteOrgChartHr[hrSimulation.id.toString()]?.length
        : 0;

    const [showSimulationModal, setShowSimulationModal] = useState(false);
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [showEditSimulationModal, setShowEditSimulationModal] =
        useState(false);
    const deleteSimulationMutation = useDeleteHrSimulation(hrSimulation.id);
    const {
        mutate: deleteSimulationMutate,
        isPending: isDeleteSimulationLoading,
    } = deleteSimulationMutation;
    const duplicateSimulationMutation = useDuplicateHrSimulation(
        hrSimulation.id,
    );
    const { isPending: isDuplicateLoading, isError } =
        duplicateSimulationMutation;

    const onSave = ({
        recurrentInitialisationStartDate,
        recurrentInitialisationEndDate,
        title,
        orgChartHrsSoftwareIds,
        isEtablissementsFilterActive,
        etablissementSoftwareIds,
        type,
        excludedAgentDepartureHypotheses,
        excludedAgentArrivalHypotheses,
        excludedMantyStatusHypotheses,
        excludedPayComponentHypotheses,
        excludedAgentPayComponentHypotheses,
        excludedGradeAdvancementHypotheses,
        isPayBudgetFilterActive,
        payBudgetSoftwareIds,
    }: HrSimulationDuplicatePayload) => {
        duplicateSimulationMutation.mutate({
            title,
            orgChartHrsSoftwareIds,
            isEtablissementsFilterActive,
            etablissementSoftwareIds,
            isPayBudgetFilterActive,
            payBudgetSoftwareIds,
            recurrentInitialisationStartDate,
            recurrentInitialisationEndDate,
            type,
            excludedAgentDepartureHypotheses,
            excludedAgentArrivalHypotheses,
            excludedMantyStatusHypotheses,
            excludedPayComponentHypotheses,
            excludedAgentPayComponentHypotheses,
            excludedGradeAdvancementHypotheses,
        });
    };
    const formattedCreationDate = capitalize(
        new Date(hrSimulation.createdAt).toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        }),
    );

    const updateSimulationMutation = usePatchHrSimulation();

    const onSaveEdit = useCallback(
        ({ title }: PatchHrSimulationPayload) => {
            updateSimulationMutation.mutate(
                {
                    id: hrSimulation.id,
                    title,
                },
                {
                    onSuccess: () => {
                        setShowEditSimulationModal(false);
                    },
                },
            );
        },

        [hrSimulation.id, updateSimulationMutation],
    );

    const toggleSimulationModal = useCallback(() => {
        setShowSimulationModal((current) => !current);
    }, []);

    const toggleEditSimulationModal = useCallback(() => {
        setShowEditSimulationModal((current) => !current);
    }, []);

    const toggleDeleteHrSimulationModal = useCallback(() => {
        setShowConfirmDeleteModal((current) => !current);
    }, []);

    const onDuplicateButtonClick = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        e.stopPropagation();
        e.preventDefault();
        toggleSimulationModal();
    };

    const onDeleteButtonClick = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
        e.stopPropagation();
        e.preventDefault();
        toggleDeleteHrSimulationModal();
    };

    const onEditButtonClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            e.preventDefault();
            toggleEditSimulationModal();
        },
        [toggleEditSimulationModal],
    );

    const deleteHrSimulation = () => {
        deleteSimulationMutate();
    };

    const formattedDate = capitalize(
        new Date(hrSimulation.recurrentInitialisationStartDate).toLocaleString(
            'default',
            {
                month: 'long',
                year: 'numeric',
            },
        ),
    );

    return (
        <>
            <Link
                to={`/human_resources/simulation/${hrSimulation.id}/scenario`}
            >
                <StyCard sx={{ height: '13rem' }}>
                    <CardActionArea sx={{ height: '100%' }} component="span">
                        <SimulationCardContent>
                            <SimulationTitleWrapper>
                                <SimulationItemTitle
                                    hrSimulation={hrSimulation}
                                    onDeleteButtonClick={onDeleteButtonClick}
                                    onDuplicateButtonClick={
                                        onDuplicateButtonClick
                                    }
                                    onEditButtonClick={onEditButtonClick}
                                />

                                <SimulationTypeChip type={hrSimulation.type} />
                            </SimulationTitleWrapper>
                            {obsoleteOrgChartHrLength > 0 && (
                                <Box paddingTop="0.5rem">
                                    <ObsoleteInfoBox>
                                        {t(
                                            'humanResources.payState.obsoleteService',
                                        )}
                                    </ObsoleteInfoBox>
                                </Box>
                            )}

                            <SimulationChipWrapper>
                                <Flex
                                    direction="column"
                                    justifyContent="space-between"
                                    paddingTop="0.5rem"
                                >
                                    <Box>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 600 }}
                                            display="inline"
                                        >
                                            {t('humanResources.creationDate')}
                                        </Typography>
                                        &nbsp;&nbsp;
                                        {formattedCreationDate}
                                    </Box>
                                    <Box>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: 600 }}
                                            display="inline"
                                        >
                                            {t(
                                                'humanResources.simulation.initializationBaseRecurrent',
                                            )}
                                        </Typography>
                                        &nbsp;&nbsp;
                                        {formattedDate}
                                    </Box>
                                </Flex>
                            </SimulationChipWrapper>
                        </SimulationCardContent>
                    </CardActionArea>
                </StyCard>
            </Link>
            {showSimulationModal && (
                <SimulationModal
                    mainTitle={t(
                        'humanResources.simulation.duplicateSimulation.title',
                    )}
                    creationSubtitle={t(
                        'humanResources.simulation.duplicateSimulation.firstStep.subtitle',
                    )}
                    creationDescription={t(
                        'humanResources.simulation.duplicateSimulation.firstStep.description',
                    )}
                    actionTitle={t('common.duplicate')}
                    onSave={onSave}
                    show={showSimulationModal}
                    onClose={toggleSimulationModal}
                    hrSimulation={hrSimulation}
                    isLoading={isDuplicateLoading}
                    isError={isError}
                />
            )}
            {showConfirmDeleteModal && (
                <ConfirmDeleteModal
                    title={t('humanResources.simulation.deleteSimulation')}
                    confirm={deleteHrSimulation}
                    show={showConfirmDeleteModal}
                    isLoading={isDeleteSimulationLoading}
                    cancel={toggleDeleteHrSimulationModal}
                />
            )}
            {showEditSimulationModal && (
                <ModalForm
                    show
                    closeModal={toggleEditSimulationModal}
                    submit={onSaveEdit}
                    submitText={t('common.save')}
                    title={t('humanResources.editSimulation')}
                    fields={[
                        {
                            key: 'title',
                            name: 'title',
                            label: t('title'),
                            type: 'text',
                            value: hrSimulation.title,
                            validators: ['required'],
                        },
                    ]}
                />
            )}
        </>
    );
};

export default HrSimulationItem;
